import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { getLocale } from '../getters/user';
import { Feature } from '../analytics';
import { jankenMoves, jankenOutcomes } from './janken.ruleset';
import { speciesChatbot } from '../chatbot/flex/species';
import { getEquippedPetSpecies } from '../pet/pet.getters';

const schema = {
  jankenNotifyResult: SB.object({
    challengeId: SB.string(),
    receiverId: SB.string(),
    outcome: SB.tuple(jankenOutcomes),
    opponentName: SB.string(),
    senderMove: SB.tuple(jankenMoves),
    receiverMove: SB.tuple(jankenMoves),
    opponentSpecies: SB.tuple(speciesChatbot),
  }),
};

const actions = createScheduledActions(schema)({
  /**
   * Send a janken result OA notification to the challenge sender.
   *
   * Scheduled with `targetUserId: senderId` from `resolveJankenChallenge`
   * so it runs on the **sender's** state — this ensures `getLocale`,
   * `getChatbotMessageChannel`, and `deliverChatbotMessage` all use the
   * correct user context.
   *
   * The `outcome` and `opponentName` are passed via args because the
   * receiver's state (where the challenge was resolved) is not available here.
   */
  jankenNotifyResult: async (
    state,
    {
      challengeId,
      receiverId,
      outcome,
      opponentName,
      senderMove,
      receiverMove,
      opponentSpecies,
    },
    api,
  ) => {
    const message = getChatbotFlexMessage({
      template: 'jankenResult',
      payload: {
        ...generateReplicantChatbotPayload({
          feature: Feature.INVITE._,
          $subFeature: Feature.INVITE.RESULT_JANKEN,
        }),
        // Match the invite share payload so getJankenEntryData() works for OA entries.
        // webReferrer = receiverId (the opponent from the sender's perspective).
        challengeId,
        webReferrer: receiverId,
        senderSpecies: opponentSpecies,
        senderDisplayName: opponentName,
      } as ReturnType<typeof generateReplicantChatbotPayload>,
      data: {
        locale: getLocale(state),
        outcome,
        opponentName,
        senderMove,
        receiverMove,
        senderSpecies: getEquippedPetSpecies(state) ?? 'dog',
        opponentSpecies,
      },
    });

    deliverChatbotMessage(state, api, {
      receiverId: state.id,
      message,
      channel: 'oa',
    });
  },
});

export const jankenScheduledActions = {
  schema,
  actions: extractScheduledActionHandlers(actions),
};
